import React, { useState } from 'react';
import {
    FileSpreadsheet, Download, Upload, Loader,
    CheckCircle, AlertCircle, X, Clock, Send, ChevronDown, Grid3x3
} from 'lucide-react';
import api from '../../api/api';
import { useTheme } from '../../hooks/useTheme';

// Collapsible panel showing only the rows that failed the format check
function FailedRowsToggle({ errorRows, isDarkMode, tableWrap, tableHead, rowHover, divider }) {
    const [open, setOpen] = useState(false);
    return (
        <div>
            <button
                onClick={() => setOpen(o => !o)}
                className={`flex items-center gap-2 text-sm font-medium transition-colors ${isDarkMode ? 'text-red-400 hover:text-red-300' : 'text-red-600 hover:text-red-700'}`}
            >
                <ChevronDown size={15} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
                {open ? 'Hide' : 'View'} {errorRows.length} failed row{errorRows.length !== 1 ? 's' : ''}
            </button>
            {open && (
                <div className={`mt-2 ${tableWrap}`}>
                    <div className={tableHead}>Failed rows — these will NOT be submitted</div>
                    <div className="max-h-56 overflow-y-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className={`text-xs uppercase tracking-wide ${isDarkMode ? 'bg-slate-700/50 text-slate-400' : 'bg-slate-50 text-slate-500'}`}>
                                    <th className="px-4 py-2 text-left w-16">#</th>
                                    <th className="px-4 py-2 text-left">Job Title</th>
                                    <th className="px-4 py-2 text-left">Company</th>
                                    <th className="px-4 py-2 text-left">Reason</th>
                                </tr>
                            </thead>
                            <tbody className={divider}>
                                {errorRows.map((r, idx) => (
                                    <tr key={idx} className={rowHover}>
                                        <td className={`px-4 py-2 font-mono ${isDarkMode ? 'text-slate-400' : 'text-slate-400'}`}>{r.row}</td>
                                        <td className={`px-4 py-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>{r.title || <span className="italic text-slate-400">—</span>}</td>
                                        <td className={`px-4 py-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>{r.company || <span className="italic text-slate-400">—</span>}</td>
                                        <td className="px-4 py-2 text-red-500 text-xs">{r.detail}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}

/**
 * ExcelImport — unified Excel import card for admin and employer roles.
 *
 * Props:
 *   mode       "admin"    — upload → save immediately → show results (saved/skipped/errors)
 *              "employer" — upload → preview rows → confirm → pending admin approval
 *   onSuccess  optional callback fired after a successful admin import (use to trigger fetchJobs)
 */

const STEP = {
    IDLE:       'idle',       // nothing uploaded yet
    PREVIEWING: 'previewing', // employer: parsed rows shown, awaiting confirm
    RESULTS:    'results',    // admin: import complete, results displayed
    SUBMITTED:  'submitted',  // employer: sent for admin approval
};

export default function ExcelImport({ mode = 'admin', onSuccess }) {
    const { isDarkMode } = useTheme();

    const [gridModal, setGridModal]      = useState(false);
    const [step, setStep]               = useState(STEP.IDLE);
    const [uploadLoading, setUploadLoading] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [data, setData]               = useState(null);  // preview or results payload
    const [pendingFile, setPendingFile]  = useState(null);  // held for employer confirm step
    const [error, setError]             = useState(null);

    const subtext  = `text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`;
    const tableWrap = `rounded-xl border overflow-hidden ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`;
    const tableHead = `px-4 py-3 border-b text-sm font-medium ${isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-300' : 'bg-slate-50 border-slate-200 text-slate-600'}`;
    const rowHover  = isDarkMode ? 'hover:bg-slate-700/40' : 'hover:bg-slate-50';
    const divider   = `divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-slate-100'}`;

    // download template 
    const handleDownloadTemplate = async () => {
        try {
            const response = await api.get('/jobs/import/excel/template', { responseType: 'blob' });
            const url  = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href  = url;
            link.setAttribute('download', 'BizBrand_Job_Import_Template.xlsx');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Template download error:', err);
            setError('Failed to download template. Please try again.');
        }
    };

    // Admin: upload → save immediately 
    const handleAdminUpload = async (file) => {
        setError(null);
        setData(null);
        setUploadLoading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            const response = await api.post('/jobs/import/excel', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                timeout: 10 * 60 * 1000,
            });
            setData(response.data);
            setStep(STEP.RESULTS);
            if (onSuccess) onSuccess();
        } catch (err) {
            console.error('Admin import error:', err);
            setData({
                saved: 0, skipped: 0, errors: 1, total_rows: 0,
                row_results: [{
                    row: 0, status: 'error',
                    detail: err.response?.data?.error || err.message,
                    enriched: false,
                }],
            });
            setStep(STEP.RESULTS);
        } finally {
            setUploadLoading(false);
        }
    };

    // Employer step 1: upload → preview (parse only, no save) 
    const handleEmployerUpload = async (file) => {
        setError(null);
        setUploadLoading(true);
        setPendingFile(file);
        try {
            const formData = new FormData();
            formData.append('file', file);
            const response = await api.post('/jobs/import/excel/preview', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                timeout: 5 * 60 * 1000,
            });
            setData(response.data);
            setStep(STEP.PREVIEWING);
        } catch (err) {
            console.error('Preview error:', err);
            setError(err.response?.data?.error || "Failed to parse the file. Make sure you're using the correct template.");
            setPendingFile(null);
        } finally {
            setUploadLoading(false);
        }
    };

    // Employer step 2: confirm → submit for approval 
    const handleConfirmSubmit = async () => {
        if (!pendingFile) return;
        setError(null);
        setSubmitLoading(true);
        try {
            const formData = new FormData();
            formData.append('file', pendingFile);
            await api.post('/jobs/import/excel/submit', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                timeout: 5 * 60 * 1000,
            });
            setStep(STEP.SUBMITTED);
        } catch (err) {
            console.error('Submit error:', err);
            setError(err.response?.data?.error || 'Submission failed. Please try again.');
        } finally {
            setSubmitLoading(false);
        }
    };

    const handleReset = () => {
        setStep(STEP.IDLE);
        setData(null);
        setPendingFile(null);
        setError(null);
        setGridModal(false);
    };

    const handleFileSelect = (file) => {
        if (!file) return;
        if (mode === 'admin') handleAdminUpload(file);
        else handleEmployerUpload(file);
    };

    // Render 
    return (
        <div className={`rounded-2xl border shadow-sm p-6 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>

            {/* Header */}
            <div className="flex items-center gap-3 mb-1">
                <FileSpreadsheet className="text-primary-500" size={22} />
                <h2 className={`text-xl font-bold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>
                    {mode === 'admin' ? 'Import Jobs from Excel' : 'Submit Jobs for Review'}
                </h2>
            </div>
            <p className={`${subtext} mb-5`}>
                {mode === 'admin'
                    ? 'Download the template, fill it in, and upload it to bulk-import jobs. Missing fields are auto-filled from Workday if a valid job URL is provided.'
                    : 'Download the template, fill in your job listings, and upload it here. An admin will review and approve your submission before jobs go live.'}
            </p>

            {/* Error banner */}
            {error && (
                <div className="mb-5 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                    <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={18} />
                    <p className="text-sm text-red-700 flex-1">{error}</p>
                    <button onClick={() => setError(null)} className="p-1 hover:bg-red-100 rounded">
                        <X size={14} className="text-red-500" />
                    </button>
                </div>
            )}

            {/* STEP: IDLE — download + upload buttons*/}
            {step === STEP.IDLE && (
                <div className="flex flex-wrap gap-3">
                    <button
                        onClick={handleDownloadTemplate}
                        className={`flex items-center gap-2 px-4 py-2 border rounded-lg text-sm font-medium transition-colors
                            ${isDarkMode
                                ? 'border-slate-600 text-slate-300 hover:bg-slate-700'
                                : 'border-slate-300 text-slate-700 hover:bg-slate-50'}`}
                    >
                        <Download size={16} />
                        Download Template
                    </button>

                    <label className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium cursor-pointer transition-colors
                        ${uploadLoading
                            ? 'opacity-50 cursor-not-allowed bg-primary-600 text-white'
                            : 'bg-primary-600 hover:bg-primary-700 text-white'}`}
                    >
                        {uploadLoading ? (
                            <><Loader className="animate-spin" size={16} />
                                {mode === 'admin' ? 'Importing...' : 'Parsing...'}</>
                        ) : (
                            <><Upload size={16} />
                                {mode === 'admin' ? 'Upload & Import' : 'Upload File'}</>
                        )}
                        <input
                            type="file"
                            accept=".xlsx"
                            className="hidden"
                            disabled={uploadLoading}
                            onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) handleFileSelect(file);
                                e.target.value = '';
                            }}
                        />
                    </label>
                </div>
            )}

            {/* STEP: RESULTS (admin only) — summary cards + row-by-row table*/}
            {step === STEP.RESULTS && data && (
                <div className="space-y-4">
                    {/* Summary cards */}
                    <div className="grid grid-cols-3 gap-4">
                        <div className="p-4 bg-primary-50 rounded-lg border border-primary-200">
                            <div className="text-2xl font-bold text-primary-700">{data.saved ?? 0}</div>
                            <div className="text-sm text-primary-600">Saved</div>
                        </div>
                        <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                            <div className="text-2xl font-bold text-yellow-700">{data.skipped ?? 0}</div>
                            <div className="text-sm text-yellow-600">Skipped</div>
                        </div>
                        <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                            <div className="text-2xl font-bold text-red-700">{data.errors ?? 0}</div>
                            <div className="text-sm text-red-600">Errors</div>
                        </div>
                    </div>

                    {/* Row-by-row results */}
                    {data.row_results?.length > 0 && (
                        <div className={tableWrap}>
                            <div className={tableHead}>
                                Row-by-Row Results ({data.total_rows} rows processed)
                            </div>
                            <div className="max-h-64 overflow-y-auto">
                                <table className="w-full text-sm">
                                    <tbody className={divider}>
                                        {data.row_results.map((r, idx) => (
                                            <tr key={idx} className={rowHover}>
                                                <td className={`px-4 py-2 w-16 font-mono ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                                    Row {r.row}
                                                </td>
                                                <td className="px-4 py-2 w-24">
                                                    {r.status === 'saved' && (
                                                        <span className="flex items-center gap-1 text-primary-600 font-medium">
                                                            <CheckCircle size={14} /> Saved
                                                        </span>
                                                    )}
                                                    {r.status === 'skipped' && (
                                                        <span className={`flex items-center gap-1 font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                                            <X size={14} /> Skipped
                                                        </span>
                                                    )}
                                                    {r.status === 'error' && (
                                                        <span className="flex items-center gap-1 text-red-600 font-medium">
                                                            <AlertCircle size={14} /> Error
                                                        </span>
                                                    )}
                                                </td>
                                                <td className={`px-4 py-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                                                    {r.detail}
                                                    {r.enriched && (
                                                        <span className="ml-2 px-1.5 py-0.5 bg-blue-100 text-blue-600 text-xs rounded">
                                                            ✦ enriched
                                                        </span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Clear results */}
                    <button
                        onClick={handleReset}
                        className={`flex items-center gap-2 px-4 py-2 text-sm rounded-lg transition-colors
                            ${isDarkMode
                                ? 'text-slate-400 hover:text-slate-200 hover:bg-slate-700'
                                : 'text-slate-500 hover:text-slate-800 hover:bg-slate-100'}`}
                    >
                        <X size={16} /> Clear Results
                    </button>
                </div>
            )}

            {/* ══════════════════════════════════════════════════════════════════
                STEP: PREVIEWING (employer only) — counts, errors toggle, confirm
            ══════════════════════════════════════════════════════════════════ */}
            {step === STEP.PREVIEWING && data && (() => {
                const validRows  = data.row_results?.filter(r => r.status === 'valid')  ?? [];
                const errorRows  = data.row_results?.filter(r => r.status === 'error')  ?? [];
                return (
                    <div className="space-y-4">
                        {/* File name */}
                        <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                            Scanned <span className="font-medium">{pendingFile?.name}</span> — {data.total_rows} row{data.total_rows !== 1 ? 's' : ''} found
                        </p>

                        {/* Count summary cards */}
                        <div className="grid grid-cols-2 gap-3">
                            <div className="p-4 bg-primary-50 border border-primary-200 rounded-lg">
                                <div className="text-2xl font-bold text-primary-700">{validRows.length}</div>
                                <div className="text-sm text-primary-600">Ready to submit</div>
                            </div>
                            <div className={`p-4 rounded-lg border ${errorRows.length > 0 ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200'}`}>
                                <div className={`text-2xl font-bold ${errorRows.length > 0 ? 'text-red-700' : isDarkMode ? 'text-slate-400' : 'text-slate-400'}`}>{errorRows.length}</div>
                                <div className={`text-sm ${errorRows.length > 0 ? 'text-red-600' : isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Failed format check</div>
                            </div>
                        </div>

                        {/* View Grid button */}
                        <button
                            onClick={() => setGridModal(true)}
                            className={`flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors
                                ${isDarkMode
                                    ? 'border-slate-600 text-slate-300 hover:bg-slate-700'
                                    : 'border-slate-300 text-slate-600 hover:bg-slate-50'}`}
                        >
                            <Grid3x3 size={13} />
                            View Grid
                        </button>

                        {/* View failed rows toggle */}
                        {errorRows.length > 0 && (
                            <FailedRowsToggle errorRows={errorRows} isDarkMode={isDarkMode} tableWrap={tableWrap} tableHead={tableHead} rowHover={rowHover} divider={divider} />
                        )}

                        {/* Actions */}
                        <div className="flex gap-3 pt-1">
                            <button
                                onClick={handleConfirmSubmit}
                                disabled={submitLoading || validRows.length === 0}
                                className="flex items-center gap-2 px-5 py-2.5 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                            >
                                {submitLoading ? (
                                    <><Loader className="animate-spin" size={16} /> Submitting...</>
                                ) : (
                                    <><Send size={16} /> Submit {validRows.length} valid job{validRows.length !== 1 ? 's' : ''} for Review</>
                                )}
                            </button>

                            <button
                                onClick={handleReset}
                                disabled={submitLoading}
                                className={`flex items-center gap-2 px-4 py-2.5 text-sm rounded-lg transition-colors disabled:opacity-50
                                    ${isDarkMode
                                        ? 'text-slate-400 hover:text-slate-200 hover:bg-slate-700'
                                        : 'text-slate-500 hover:text-slate-800 hover:bg-slate-100'}`}
                            >
                                <X size={15} /> Cancel
                            </button>
                        </div>
                    </div>
                );
            })()}

            {/* Grid Preview Modal */}
            {gridModal && data?.row_results && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setGridModal(false)}>
                    <div
                        className={`rounded-2xl shadow-2xl w-full max-w-3xl max-h-[80vh] flex flex-col ${isDarkMode ? 'bg-slate-800' : 'bg-white'}`}
                        onClick={e => e.stopPropagation()}
                    >
                        <div className={`flex items-center justify-between px-6 py-4 border-b ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                            <div>
                                <h3 className={`font-semibold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>
                                    Job Submission Preview
                                </h3>
                                <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                    {pendingFile?.name} — {data.total_rows} row{data.total_rows !== 1 ? 's' : ''}
                                </p>
                            </div>
                            <button
                                onClick={() => setGridModal(false)}
                                className={`p-2 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-slate-100 text-slate-500'}`}
                            >
                                <X size={18} />
                            </button>
                        </div>
                        <div className="overflow-auto flex-1">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className={`border-b ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                                        {['#', 'Job Title', 'Company', 'Location', 'Status'].map(col => (
                                            <th key={col} className={`px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide sticky top-0 ${isDarkMode ? 'bg-slate-700 text-slate-400' : 'bg-slate-50 text-slate-500'}`}>
                                                {col}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.row_results.map((r, idx) => (
                                        <tr key={idx} className={`border-b ${isDarkMode ? 'border-slate-700' : 'border-slate-100'}`}>
                                            <td className={`px-4 py-2.5 text-xs font-mono ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>{r.row ?? idx + 1}</td>
                                            <td className={`px-4 py-2.5 text-sm font-medium ${isDarkMode ? 'text-slate-200' : 'text-slate-800'}`}>{r.title || '—'}</td>
                                            <td className={`px-4 py-2.5 text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>{r.company || '—'}</td>
                                            <td className={`px-4 py-2.5 text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>{r.location || '—'}</td>
                                            <td className="px-4 py-2.5">
                                                <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                                                    r.status === 'valid'
                                                        ? 'bg-primary-100 text-primary-700'
                                                        : 'bg-red-100 text-red-600'
                                                }`}>
                                                    {r.status}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className={`px-6 py-3 border-t flex justify-end ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                            <button
                                onClick={() => setGridModal(false)}
                                className={`px-4 py-2 text-sm rounded-lg border transition-colors
                                    ${isDarkMode ? 'border-slate-600 text-slate-300 hover:bg-slate-700' : 'border-slate-300 text-slate-600 hover:bg-slate-50'}`}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/*employer only*/}
            {step === STEP.SUBMITTED && (
                <div className={`rounded-xl border p-6 text-center ${isDarkMode ? 'bg-slate-700/50 border-slate-600' : 'bg-primary-50 border-primary-200'}`}>
                    <div className="flex justify-center mb-3">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-primary-900/50' : 'bg-primary-100'}`}>
                            <Clock size={24} className="text-primary-500" />
                        </div>
                    </div>
                    <h3 className={`text-base font-semibold mb-1 ${isDarkMode ? 'text-slate-100' : 'text-primary-900'}`}>
                        Submission Received
                    </h3>
                    <p className={`text-sm mb-5 ${isDarkMode ? 'text-slate-400' : 'text-primary-700'}`}>
                        Your job listings are pending admin review. You'll be notified once they're approved and go live.
                    </p>
                    <button
                        onClick={handleReset}
                        className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors
                            ${isDarkMode
                                ? 'bg-slate-600 hover:bg-slate-500 text-slate-200'
                                : 'bg-white border border-primary-300 text-primary-700 hover:bg-primary-50'}`}
                    >
                        <Upload size={15} /> Submit Another File
                    </button>
                </div>
            )}
        </div>
    );
}